import { useCallback, useEffect, useState } from "react";
import {
  MDBBtn, MDBInput, MDBModal, MDBModalDialog, MDBModalContent,
  MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter,
  MDBTextArea,
} from "mdb-react-ui-kit";

import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { mfmThumbUrl, mfmMediumUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { getApp, getAppPermisions } from "Projects/CC_cubi_clan_project/Utils/PermissionManager";
import {
  getCharacter, updateCharacter, addPokemon, updatePokemon, removePokemon,
  setItem, setBadge, addCard, removeCard, getCatalogBadges, isOk,
  swapPokemon, healAllPokemon, reorderTeam, getCatalogPokemonDetail,
  useItem as applyUseItem, addEstado, removeEstado, getCatalogEstados,
  addEgg, updateEggTurnos, hatchEgg, evolveCharacterPokemon, adminSavePokemon,
  getCatalogPokemon, getCatalogItems, getCatalogRegiones,
} from "Projects/CHAMP_championsjourney_project/Utils/ChampService";
import CatalogPicker from "Projects/CHAMP_championsjourney_project/Components/CatalogPicker/CatalogPicker";
import CatalogAutocomplete from "Projects/CHAMP_championsjourney_project/Components/CatalogAutocomplete/CatalogAutocomplete";

const TYPE_COLORS = {
  NORMAL: "#A8A878", FUEGO: "#F08030", AGUA: "#6890F0", PLANTA: "#78C850",
  ELECTRICO: "#F8D030", HIELO: "#98D8D8", LUCHA: "#C03028", VENENO: "#A040A0",
  TIERRA: "#E0C068", VOLADOR: "#A890F0", PSIQUICO: "#F85888", BICHO: "#A8B820",
  ROCA: "#B8A038", FANTASMA: "#705898", DRAGON: "#7038F8", SINIESTRO: "#705848",
  ACERO: "#B8B8D0", HADA: "#EE99AC",
};

// Imagen por defecto (pokéball) con el mismo formato que las cartas (428x303).
// Se usa cuando un Pokémon no tiene imagen o cuando esta no se puede cargar.
const CHAMP_DEFAULT_SPRITE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 428 303">' +
      '<rect width="428" height="303" fill="#F5ECD7"/>' +
      '<clipPath id="b"><circle cx="214" cy="151.5" r="62"/></clipPath>' +
      '<g clip-path="url(#b)">' +
        '<rect x="152" y="89.5" width="124" height="62" fill="#d98a85"/>' +
        '<rect x="152" y="151.5" width="124" height="62" fill="#f3ede0"/>' +
        '<rect x="152" y="142" width="124" height="19" fill="#6b6253"/>' +
      '</g>' +
      '<circle cx="214" cy="151.5" r="62" fill="none" stroke="#6b6253" stroke-width="7"/>' +
      '<circle cx="214" cy="151.5" r="19" fill="#f3ede0" stroke="#6b6253" stroke-width="7"/>' +
    '</svg>'
  );

function typeColor(t) {
  return TYPE_COLORS[(t || "").toUpperCase()] || "#777";
}

export default function CharacterSheet({ characterId, onLoading, modoEvolucion = "VISIBLE" }) {
  const [character, setCharacter] = useState(null);
  const [editStats, setEditStats] = useState(false);
  const [form, setForm] = useState({});
  const [badges, setBadges] = useState([]);
  const [pickerType, setPickerType] = useState(null); // "pokemon" | "items" | "cards"
  const [pickerTarget, setPickerTarget] = useState(true); // true = equipo, false = caja
  const [dragId, setDragId] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [hpEdits, setHpEdits] = useState({}); // { idPp: valor en edición }
  const [detailPoke, setDetailPoke] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [estadoCatalog, setEstadoCatalog] = useState([]);
  const [regionCatalog, setRegionCatalog] = useState([]);
  const [useItemFor, setUseItemFor] = useState(null); // pp sobre el que aplicar objeto
  const [estadoPickerFor, setEstadoPickerFor] = useState(null); // pp al que añadir estado
  const [showEggTurnosModal, setShowEggTurnosModal] = useState(false); // paso 1: introducir turnos y modo
  const [showEggPicker, setShowEggPicker] = useState(false); // paso 2 (modo AHORA): seleccionar pokémon del huevo
  const [eggTurnos, setEggTurnos] = useState(10);
  const [eggPickerTarget, setEggPickerTarget] = useState(true);
  const [eggMode, setEggMode] = useState("AHORA"); // "AHORA" | "AL_ABRIR" | "ALEATORIO"
  const [showHatchPicker, setShowHatchPicker] = useState(false); // picker al eclosionar
  const [hatchingPp, setHatchingPp] = useState(null); // pp con turnos=0 siendo eclosionado
  // Evolución
  const [evoModal, setEvoModal] = useState(false);      // modal abierto
  const [evoOptions, setEvoOptions] = useState([]);     // posibles evoluciones
  const [evoTargetPp, setEvoTargetPp] = useState(null); // pp a evolucionar
  const [evoSelected, setEvoSelected] = useState(null); // evolución elegida (PokemonCatalogoDTO)
  const [evoLoading, setEvoLoading] = useState(false);
  const [evoResult, setEvoResult] = useState(null);     // null | { advertencias }
  // Admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminEditPoke, setAdminEditPoke] = useState(null);   // PokemonCatalogoDTO siendo editado
  const [adminEditForm, setAdminEditForm] = useState({});
  const [adminEditLoading, setAdminEditLoading] = useState(false);
  const [adminPokeOptions, setAdminPokeOptions] = useState([]);
  const [adminItemOptions, setAdminItemOptions] = useState([]);

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    try {
      const res = await getCharacter(characterId);
      if (isOk(res)) {
        setCharacter(res.DATA);
        const region = res.DATA.regionActual;
        // Cuando ambas regiones → null para cargar todas las insignias
        const badgeRegion = region && region.includes(",") ? null : region;
        const bRes = await getCatalogBadges(badgeRegion);
        if (isOk(bRes) && Array.isArray(bRes.DATA)) {
          const owned = new Set((res.DATA.medallas || []).map((m) => m.idMedalla));
          setBadges(bRes.DATA.map((b) => ({ ...b, obtenida: owned.has(b.idMedalla) })));
        }
      } else {
        toast.show({ message: res?.MESSAGE || "No se pudo cargar el personaje", type: "danger" });
      }
    } finally {
      onLoading && onLoading(false);
    }
  }, [characterId, onLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getCatalogEstados().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setEstadoCatalog(res.DATA);
    });
    getCatalogRegiones().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setRegionCatalog(res.DATA);
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const app = await getApp("CHAMP", () => {});
        if (app) {
          const perms = await getAppPermisions(app, () => {});
          if (Array.isArray(perms) && perms.some((p) => p && (p.nombre === "APP_ADMIN" || p.nombre === "APP_MANAGER"))) {
            setIsAdmin(true);
            // Cargar opciones para los autocomplete del modal de edición
            const [pokeRes, itemRes] = await Promise.all([getCatalogPokemon(), getCatalogItems()]);
            if (isOk(pokeRes) && Array.isArray(pokeRes.DATA)) {
              setAdminPokeOptions(pokeRes.DATA.map((p) => ({ id: p.idPokemon, label: `#${p.idPokemon} ${p.nombre}` })));
            }
            if (isOk(itemRes) && Array.isArray(itemRes.DATA)) {
              setAdminItemOptions(itemRes.DATA.map((i) => ({ id: i.idObjeto, label: i.nombre })));
            }
          }
        }
      } catch (e) {
        // sin permisos de admin
      }
    })();
  }, []);

  const closeDetail = useCallback(() => setDetailPoke(null), []);
  const closeUseItem = useCallback(() => setUseItemFor(null), []);
  const closeEstadoPicker = useCallback(() => setEstadoPickerFor(null), []);
  const closeEggTurnosModal = useCallback(() => setShowEggTurnosModal(false), []);
  const closeEggPicker = useCallback(() => setShowEggPicker(false), []);
  const closeHatchPicker = useCallback(() => setShowHatchPicker(false), []);
  const closeAdminEdit = useCallback(() => { setAdminEditPoke(null); setAdminEditForm({}); }, []);
  const closeEvoModal = useCallback(() => {
    setEvoModal(false);
    setEvoOptions([]);
    setEvoTargetPp(null);
    setEvoSelected(null);
    setEvoResult(null);
  }, []);

  if (!character) return null;

  const editable = character.editable;

  function openAdminEdit(poke) {
    setAdminEditForm({
      nombre: poke.nombre || "",
      tipo1: poke.tipo1 || "",
      tipo2: poke.tipo2 || "",
      fase: poke.fase ?? "",
      evolucionaDe: poke.evolucionaDe ?? null,
      metodoEvolucion: poke.metodoEvolucion || "NINGUNO",
      xpEvolucion: poke.xpEvolucion ?? "",
      idObjetoEvolucion: poke.idObjetoEvolucion ?? null,
      descripcionRequisito: poke.descripcionRequisito || "",
      inicial: poke.inicial || false,
      ps: poke.ps ?? "",
      ataqueBase: poke.ataqueBase || "",
      criticoDado: poke.criticoDado || "",
      region: poke.region || "",
      pokedexTexto: poke.pokedexTexto || "",
      frontUuid: poke.frontUuid || "",
      backUuid: poke.backUuid || "",
      debilidades: (poke.debilidades || []).join(", "),
      inmunidades: (poke.inmunidades || []).join(", "),
      mos: (poke.mos || []).join(", "),
    });
    setAdminEditPoke(poke);
  }

  async function saveAdminEdit() {
    if (!adminEditPoke) return;
    setAdminEditLoading(true);
    const f = adminEditForm;
    const fields = {
      nombre: f.nombre,
      tipo1: f.tipo1,
      tipo2: f.tipo2,
      fase: f.fase !== "" ? parseInt(f.fase, 10) : null,
      evolucionaDe: f.evolucionaDe ?? null,
      metodoEvolucion: f.metodoEvolucion,
      xpEvolucion: f.xpEvolucion !== "" ? parseInt(f.xpEvolucion, 10) : null,
      idObjetoEvolucion: f.idObjetoEvolucion ?? null,
      descripcionRequisito: f.descripcionRequisito,
      inicial: f.inicial,
      ps: f.ps !== "" ? parseInt(f.ps, 10) : null,
      ataqueBase: f.ataqueBase,
      criticoDado: f.criticoDado,
      region: f.region,
      pokedexTexto: f.pokedexTexto,
      frontUuid: f.frontUuid,
      backUuid: f.backUuid,
      debilidades: f.debilidades,
      inmunidades: f.inmunidades,
      mos: f.mos,
    };
    const res = await adminSavePokemon(adminEditPoke.idPokemon, fields);
    setAdminEditLoading(false);
    if (isOk(res)) {
      toast.show({ message: "Pokémon actualizado en el catálogo", type: "success" });
      closeAdminEdit();
      // Recargar el detalle para reflejar cambios
      if (detailPoke?.idPokemon === adminEditPoke.idPokemon) {
        const freshRes = await getCatalogPokemonDetail(adminEditPoke.idPokemon);
        if (isOk(freshRes)) {
          setDetailPoke({ ...freshRes.DATA, apodo: detailPoke.apodo, pp: detailPoke.pp });
        }
      }
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo guardar el Pokémon", type: "danger" });
    }
  }

  function adminFieldChange(field, value) {
    setAdminEditForm((prev) => ({ ...prev, [field]: value }));
  }

  function beginEdit() {
    setForm({
      nombre: character.nombre || "",
      regionActual: character.regionActual || "KANTO",
      nivel: character.nivel,
      experiencia: character.experiencia,
      dinero: character.dinero,
      notas: character.notas || "",
    });
    setEditStats(true);
  }

  async function saveStats() {
    const res = await updateCharacter(characterId, form);
    if (isOk(res)) {
      toast.show({ message: "Personaje actualizado", type: "success" });
      setEditStats(false);
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo guardar", type: "danger" });
    }
  }

  async function adjustStat(field, delta) {
    const current = field === "experiencia" ? character.experiencia : character.dinero;
    const next = Math.max(0, current + delta);
    if (next === current) return;
    const res = await updateCharacter(characterId, { [field]: next });
    if (isOk(res)) setCharacter(res.DATA);
    else toast.show({ message: res?.MESSAGE || "Error al actualizar", type: "danger" });
  }

  async function handlePickPokemon(poke) {
    const toTeam = pickerTarget && (character.equipo || []).length < 6;
    const res = await addPokemon(characterId, {
      idPokemon: poke.idPokemon,
      enEquipo: toTeam,
      nivel: 1,
    });
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo añadir", type: "danger" });
  }

  async function handlePickItem(obj) {
    const existing = (character.mochila || []).find((m) => m.idObjeto === obj.idObjeto);
    const res = await setItem(characterId, {
      idObjeto: obj.idObjeto,
      cantidad: (existing ? existing.cantidad : 0) + 1,
    });
    if (isOk(res)) load();
  }

  async function handlePickCard(card) {
    const res = await addCard(characterId, card.idTarjeta);
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo añadir", type: "danger" });
  }

  async function toggleTeam(pp) {
    if (!pp.enEquipo && (character.equipo || []).length >= 6) {
      toast.show({ message: "El equipo está completo (máx. 6)", type: "warning" });
      return;
    }
    const res = await updatePokemon(pp.idPp, { enEquipo: !pp.enEquipo });
    if (isOk(res)) load();
  }

  async function toggleFainted(pp) {
    const res = await updatePokemon(pp.idPp, { debilitado: !pp.debilitado });
    if (isOk(res)) load();
  }

  async function deletePokemon(pp) {
    const res = await removePokemon(pp.idPp);
    if (isOk(res)) {
      if (detailPoke?.pp?.idPp === pp.idPp) setDetailPoke(null);
      load();
    }
  }

  function openPokemonPicker(target) {
    setPickerTarget(target);
    setPickerType("pokemon");
  }

  function openEggPicker(toTeam) {
    setEggPickerTarget(toTeam);
    setEggTurnos(10);
    setEggMode("AHORA");
    setShowEggTurnosModal(true);
  }

  async function confirmEggTurnos() {
    setShowEggTurnosModal(false);
    if (eggMode === "AHORA") {
      // Flujo original: elegir pokémon ahora
      setShowEggPicker(true);
    } else {
      // ALEATORIO y AL_ABRIR: crear huevo misterioso sin pokémon asignado
      const res = await addEgg(characterId, { idPokemon: null, turnosHuevo: eggTurnos });
      if (isOk(res)) load();
      else toast.show({ message: res?.MESSAGE || "No se pudo añadir el huevo", type: "danger" });
    }
  }

  async function handlePickEgg(poke) {
    setShowEggPicker(false);
    const res = await addEgg(characterId, { idPokemon: poke.idPokemon, turnosHuevo: eggTurnos });
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo añadir el huevo", type: "danger" });
  }

  async function decreaseEggTurns(pp) {
    const current = pp.turnosHuevo ?? 0;
    const next = Math.max(0, current - 1);
    const res = await updateEggTurnos(pp.idPp, next);
    if (isOk(res)) setCharacter(res.DATA);
    else toast.show({ message: res?.MESSAGE || "Error", type: "danger" });
  }

  /** Eclosiona un huevo que ya tiene idPokemon asignado (modo AHORA). */
  async function handleHatchAssigned(pp) {
    const res = await hatchEgg(pp.idPp, null);
    if (isOk(res)) {
      toast.show({ message: "🐣 ¡El huevo ha eclosionado!", type: "success" });
      setCharacter(res.DATA);
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo eclosionar el huevo", type: "danger" });
    }
  }

  /** Abre el picker para que el usuario elija qué pokémon sale del huevo. */
  function openHatchPicker(pp) {
    setHatchingPp(pp);
    setShowHatchPicker(true);
  }

  /** Callback del picker al eclosionar: eclosiona con el pokémon elegido. */
  async function handleHatchPick(poke) {
    setShowHatchPicker(false);
    if (!hatchingPp) return;
    const res = await hatchEgg(hatchingPp.idPp, poke.idPokemon);
    if (isOk(res)) {
      toast.show({ message: `🐣 ¡El huevo ha eclosionado! Es ${poke.nombre || "un Pokémon"}`, type: "success" });
      setCharacter(res.DATA);
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo eclosionar el huevo", type: "danger" });
    }
    setHatchingPp(null);
  }

  /** Eclosiona un huevo con un pokémon aleatorio del catálogo. */
  async function handleHatchRandom(pp) {
    const catRes = await getCatalogPokemon(
      character?.regionActual && !character.regionActual.includes(",")
        ? character.regionActual
        : null
    );
    if (!isOk(catRes) || !Array.isArray(catRes.DATA) || catRes.DATA.length === 0) {
      toast.show({ message: "No se pudo obtener el catálogo para elegir aleatoriamente", type: "danger" });
      return;
    }
    const randomPoke = catRes.DATA[Math.floor(Math.random() * catRes.DATA.length)];
    const res = await hatchEgg(pp.idPp, randomPoke.idPokemon);
    if (isOk(res)) {
      toast.show({ message: `🐣 ¡El huevo ha eclosionado! Es ${randomPoke.nombre || "un Pokémon"}`, type: "success" });
      setCharacter(res.DATA);
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo eclosionar el huevo", type: "danger" });
    }
  }

  async function healAll() {
    const res = await healAllPokemon(characterId);
    if (isOk(res)) {
      toast.show({ message: "Todos los Pokémon curados", type: "success" });
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo curar", type: "danger" });
    }
  }

  function commitHp(pp) {
    const raw = hpEdits[pp.idPp];
    setHpEdits((prev) => {
      const next = { ...prev };
      delete next[pp.idPp];
      return next;
    });
    if (raw === undefined || raw === "") return;
    const val = Math.max(0, parseInt(raw, 10) || 0);
    const current = pp.psActual ?? pp.ps;
    if (val === current) return;
    updatePokemon(pp.idPp, { psActual: val }).then((res) => {
      if (isOk(res)) load();
      else toast.show({ message: res?.MESSAGE || "No se pudo actualizar la vida", type: "danger" });
    });
  }

  // ── Drag & drop equipo ↔ caja ──────────────────────────────────────────────
  async function handleDropOnCard(e, target) {
    e.preventDefault();
    e.stopPropagation();
    if (dragItem) {
      const item = dragItem;
      setDragItem(null);
      await applyItem(item.idObjeto, target.idPp);
      return;
    }
    if (dragId == null || dragId === target.idPp) { setDragId(null); return; }
    const res = await swapPokemon(dragId, target.idPp);
    setDragId(null);
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo intercambiar", type: "danger" });
  }

  async function handleDropOnZone(toTeam) {
    if (dragItem) { setDragItem(null); return; }
    if (dragId == null) return;
    const all = [...(character.equipo || []), ...(character.caja || [])];
    const dragged = all.find((p) => p.idPp === dragId);
    setDragId(null);
    if (!dragged || dragged.enEquipo === toTeam) return;
    const res = await updatePokemon(dragged.idPp, { enEquipo: toTeam });
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo mover", type: "danger" });
  }

  // ── Uso de objetos y estados ───────────────────────────────────────────────
  async function applyItem(idObjeto, idPp) {
    const res = await applyUseItem(characterId, { idObjeto, idPp });
    if (isOk(res)) {
      setCharacter(res.DATA);
      toast.show({ message: "Objeto usado", type: "success" });
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo usar el objeto", type: "danger" });
    }
  }

  async function handleAddEstado(idPp, idEstado) {
    setEstadoPickerFor(null);
    const res = await addEstado(idPp, idEstado);
    if (isOk(res)) setCharacter(res.DATA);
    else toast.show({ message: res?.MESSAGE || "No se pudo aplicar el estado", type: "danger" });
  }

  async function handleRemoveEstado(idPp, idEstado) {
    const res = await removeEstado(idPp, idEstado);
    if (isOk(res)) setCharacter(res.DATA);
    else toast.show({ message: res?.MESSAGE || "No se pudo quitar el estado", type: "danger" });
  }

  // ── Reordenar equipo por posición numérica ─────────────────────────────────
  async function moveToPosition(pp, newPos) {
    const ids = (character.equipo || []).map((p) => p.idPp);
    const from = ids.indexOf(pp.idPp);
    if (from < 0) return;
    let to = Math.max(0, Math.min(ids.length - 1, newPos - 1));
    if (to === from) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const res = await reorderTeam(characterId, ids);
    if (isOk(res)) load();
    else toast.show({ message: res?.MESSAGE || "No se pudo reordenar", type: "danger" });
  }

  // ── Detalle de Pokémon ─────────────────────────────────────────────────────
  async function openDetail(pp, autoEvolve = false) {
    setDetailLoading(true);
    setDetailPoke({ loading: true, nombrePokemon: pp.nombrePokemon, apodo: pp.apodo, pp });
    const res = await getCatalogPokemonDetail(pp.idPokemon);
    setDetailLoading(false);
    if (isOk(res)) {
      const data = { ...res.DATA, apodo: pp.apodo, pp };
      setDetailPoke(data);
      if (autoEvolve && Array.isArray(data.evoluciones) && data.evoluciones.length > 0) {
        const livePp = [...(character.equipo || []), ...(character.caja || [])].find((x) => x.idPp === pp.idPp) || pp;
        setEvoTargetPp(livePp);
        setEvoOptions(data.evoluciones);
        setEvoSelected(data.evoluciones.length === 1 ? data.evoluciones[0] : null);
        setEvoResult(null);
        setEvoModal(true);
      }
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo cargar el detalle", type: "danger" });
      setDetailPoke(null);
    }
  }

  // ── XP individual del Pokémon ─────────────────────────────────────────────
  async function adjustPokeXp(pp, delta) {
    const newXp = Math.max(0, (pp.experiencia || 0) + delta);
    const res = await updatePokemon(pp.idPp, { experiencia: newXp });
    if (!isOk(res)) {
      toast.show({ message: res?.MESSAGE || "No se pudo actualizar la XP", type: "danger" });
      return;
    }
    await load();
    // Comprobar si se alcanzó el umbral de evolución
    if (pp.xpParaEvolucionar != null && newXp >= pp.xpParaEvolucionar) {
      toast.show({
        message: `⬆️ ${pp.nombrePokemon} tiene XP suficiente para evolucionar.`,
        type: "info",
        duration: 5000,
      });
      openDetail(pp, true);
    }
  }

  // ── Evolución de Pokémon ────────────────────────────────────────────────────
  function openEvoModal(livePp) {
    if (!detailPoke?.evoluciones?.length) return;
    setEvoTargetPp(livePp);
    setEvoOptions(detailPoke.evoluciones);
    setEvoSelected(detailPoke.evoluciones.length === 1 ? detailPoke.evoluciones[0] : null);
    setEvoResult(null);
    setEvoModal(true);
  }

  async function confirmEvolve() {
    if (!evoSelected || !evoTargetPp) return;
    setEvoLoading(true);
    const res = await evolveCharacterPokemon(evoTargetPp.idPp, evoSelected.idPokemon);
    setEvoLoading(false);
    if (isOk(res)) {
      setEvoResult(res.DATA);
      await load();
      // Actualizar la ventana de detalle con los datos del Pokémon evolucionado
      const detailRes = await getCatalogPokemonDetail(evoSelected.idPokemon);
      if (isOk(detailRes)) {
        setDetailPoke((prev) => ({
          ...detailRes.DATA,
          apodo: prev?.apodo,
          pp: prev?.pp
            ? { ...prev.pp, idPokemon: evoSelected.idPokemon, nombrePokemon: evoSelected.nombre }
            : prev?.pp,
        }));
      }
      // Si no hay advertencias cerramos automáticamente tras un momento
      if (!res.DATA?.advertencias?.length) {
        setTimeout(() => closeEvoModal(), 1200);
      }
    } else {
      toast.show({ message: res?.MESSAGE || "No se pudo evolucionar", type: "danger" });
    }
  }

  /** Comprueba localmente si se cumplen los requisitos del Pokémon evolución elegido. */
  function checkRequisitos(evo, livePp) {
    if (!evo) return [];
    const checks = [];
    const metodo = evo.metodoEvolucion || "NINGUNO";
    switch (metodo) {
      case "EXPERIENCIA": {
        const needed = evo.xpEvolucion || 0;
        const ppXp = livePp?.experiencia || 0;
        const playerXp = character?.experiencia || 0;
        const totalXp = ppXp + playerXp;
        const ok = totalXp >= needed;
        const ppSuffices = ppXp >= needed;
        const fromPp = ppSuffices ? needed : ppXp;
        const fromPlayer = ppSuffices ? 0 : Math.max(0, needed - ppXp);
        checks.push({
          ok,
          msg: `Coste de evolución: ${needed} XP ${ok ? "✔" : "✗ (insuficiente — se evolucionará igualmente)"}`,
        });
        checks.push({ ok: true, msg: `↳ Del Pokémon: ${fromPp} XP (tiene ${ppXp})` });
        if (!ppSuffices) {
          checks.push({
            ok: playerXp >= fromPlayer,
            msg: `↳ Del jugador: ${fromPlayer} XP (tiene ${playerXp})`,
          });
        }
        break;
      }
      case "PIEDRA":
      case "OBJETO": {
        if (evo.idObjetoEvolucion) {
          const inv = (character.mochila || []).find((it) => it.idObjeto === evo.idObjetoEvolucion);
          const ok = inv && inv.cantidad > 0;
          checks.push({ ok, msg: ok ? `Tienes ${evo.nombreObjetoEvolucion || "el objeto"} (×${inv.cantidad}) ✔` : `No tienes ${evo.nombreObjetoEvolucion || "el objeto requerido"} (se evolucionará igualmente)` });
        } else if (evo.descripcionRequisito) {
          checks.push({ ok: null, msg: `Objeto requerido: ${evo.descripcionRequisito}` });
        }
        break;
      }
      case "INTERCAMBIO": {
        const desc = evo.descripcionRequisito || "intercambio";
        checks.push({ ok: null, msg: `Requisito de intercambio: ${desc}` });
        break;
      }
      default:
        checks.push({ ok: true, msg: "Sin requisitos ✔" });
    }
    return checks;
  }

  /** Resumen compacto de requisito para mostrarlo dentro del botón de opción de evolución. */
  function getReqLabel(evo, livePp) {
    const metodo = evo.metodoEvolucion || "NINGUNO";
    switch (metodo) {
      case "EXPERIENCIA": {
        const needed = evo.xpEvolucion || 0;
        const ppXp = livePp?.experiencia || 0;
        const playerXp = character?.experiencia || 0;
        const totalXp = ppXp + playerXp;
        const ok = totalXp >= needed;
        return { text: `XP: ${needed} (tienes ${totalXp})`, ok };
      }
      case "PIEDRA":
      case "OBJETO": {
        const nombre = evo.nombreObjetoEvolucion || evo.descripcionRequisito || "Objeto";
        if (evo.idObjetoEvolucion) {
          const inv = (character?.mochila || []).find((it) => it.idObjeto === evo.idObjetoEvolucion);
          const ok = !!(inv && inv.cantidad > 0);
          return { text: `Objeto: ${nombre}${ok ? ` (×${inv.cantidad})` : " (no tienes)"}`, ok };
        }
        return { text: `Objeto: ${nombre}`, ok: null };
      }
      case "INTERCAMBIO":
        return { text: evo.descripcionRequisito || "Intercambio", ok: null };
      default:
        if (evo.descripcionRequisito) return { text: evo.descripcionRequisito, ok: null };
        return { text: "Sin requisitos", ok: true };
    }
  }

  async function changeQty(item, delta) {
    const res = await setItem(characterId, {
      idObjeto: item.idObjeto,
      cantidad: item.cantidad + delta,
    });
    if (isOk(res)) load();
  }

  async function toggleBadge(b) {
    const res = await setBadge(characterId, b.idMedalla, !b.obtenida);
    if (isOk(res)) load();
  }

  async function dropCard(card) {
    const res = await removeCard(card.idPt);
    if (isOk(res)) load();
  }

  function renderEstadoBadges(pp, { removable = false } = {}) {
    const estados = pp.estados || [];
    if (estados.length === 0 && !(removable && editable)) return null;
    return (
      <div className="champ-estados">
        {estados.map((es) => (
          <span
            key={es.idEstado}
            className="champ-estado-badge"
            style={{ background: es.color || "#777" }}
            title={es.descripcion || es.nombre}
          >
            {es.icono ? es.icono + " " : ""}{es.nombre}
            {removable && editable && (
              <button
                type="button"
                className="champ-estado-remove"
                title="Quitar estado"
                onClick={() => handleRemoveEstado(pp.idPp, es.idEstado)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {removable && editable && (
          <button
            type="button"
            className="champ-estado-add"
            title="Añadir estado"
            onClick={() => setEstadoPickerFor(pp)}
          >
            + estado
          </button>
        )}
      </div>
    );
  }

  function renderPokeControls(pp, inTeam, { showDetailBtn = true } = {}) {
    const maxPs = pp.ps ?? null;
    const curPs = pp.psActual ?? maxPs;
    const teamCount = (character.equipo || []).length;
    const position = inTeam ? (character.equipo || []).findIndex((p) => p.idPp === pp.idPp) + 1 : null;
    return (
      <>
        <div className="champ-hp-row">
          <span className="champ-hp-label">PS</span>
          {editable ? (
            <input
              type="number"
              className="champ-hp-input"
              min={0}
              max={maxPs ?? undefined}
              value={hpEdits[pp.idPp] ?? curPs ?? ""}
              onChange={(e) => setHpEdits({ ...hpEdits, [pp.idPp]: e.target.value })}
              onBlur={() => commitHp(pp)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            />
          ) : (
            <span className="champ-hp-cur">{curPs ?? "\u2014"}</span>
          )}
          <span className="champ-hp-max">/ {maxPs ?? "\u2014"}</span>
        </div>

        {inTeam && editable && (
          <div className="champ-order-row">
            <span className="champ-text-muted">Orden</span>
            <input
              type="number"
              className="champ-order-input"
              min={1}
              max={teamCount}
              value={position}
              onChange={(e) => moveToPosition(pp, parseInt(e.target.value || "1", 10))}
            />
          </div>
        )}

        <div className="champ-poke-actions">
          {showDetailBtn && (
            <MDBBtn size="sm" color="link" onClick={() => openDetail(pp)} title="Ver detalles">ℹ️</MDBBtn>
          )}
          {editable && (
            <>
              <MDBBtn size="sm" color="link" onClick={() => toggleTeam(pp)} title={inTeam ? "A la caja" : "Al equipo"}>
                {inTeam ? "📦" : "⬆️"}
              </MDBBtn>
              <MDBBtn size="sm" color="link" onClick={() => toggleFainted(pp)} title="Debilitado">
                {pp.debilitado ? "💚" : "💔"}
              </MDBBtn>
              <MDBBtn size="sm" color="link" onClick={() => deletePokemon(pp)} title="Liberar">🗑️</MDBBtn>
            </>
          )}
        </div>
      </>
    );
  }

  function renderPokemon(pp, inTeam) {
    if (pp.esHuevo) {
      return (
        <div
          key={pp.idPp}
          className={"champ-poke-card " + (inTeam ? "team" : "box") + " champ-poke-egg"}
        >
          <div className="champ-poke-img-wrap">
            <img
              src={CHAMP_DEFAULT_SPRITE}
              alt="Huevo Misterioso"
              className="champ-poke-sprite"
              draggable={false}
            />
            <span className="champ-egg-turns-badge">
              {pp.turnosHuevo === 0
                ? "🐣 ¡Listo!"
                : `🥚 ${pp.turnosHuevo ?? "?"} turnos`}
            </span>
          </div>
          <div className="champ-poke-body">
            <h6>Huevo Misterioso</h6>
            {editable && (
              <div className="champ-poke-actions">
                {pp.turnosHuevo === 0 ? (
                  pp.idPokemon != null ? (
                    // Huevo con pokémon pre-asignado (modo "al coger"): eclosionar directo
                    <MDBBtn size="sm" color="warning" onClick={() => handleHatchAssigned(pp)}>🐣 Abrir</MDBBtn>
                  ) : (
                    // Huevo misterioso: elegir o aleatorio
                    <>
                      <MDBBtn size="sm" color="warning" onClick={() => openHatchPicker(pp)}>🔍 Elegir</MDBBtn>
                      <MDBBtn size="sm" color="info" onClick={() => handleHatchRandom(pp)}>🎲 Sorpresa</MDBBtn>
                    </>
                  )
                ) : (
                  <MDBBtn size="sm" color="link" title="-1 turno" onClick={() => decreaseEggTurns(pp)}>-1 turno</MDBBtn>
                )}
                <MDBBtn size="sm" color="link" onClick={() => deletePokemon(pp)} title="Liberar">🗑️</MDBBtn>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        key={pp.idPp}
        className={
          "champ-poke-card " + (inTeam ? "team" : "box") +
          (pp.debilitado ? " debilitado" : "") +
          (dragId === pp.idPp ? " dragging" : "") +
          (dragItem ? " champ-drop-target" : "")
        }
        draggable={editable}
        onDragStart={editable ? () => setDragId(pp.idPp) : undefined}
        onDragEnd={editable ? () => setDragId(null) : undefined}
        onDragOver={editable ? (e) => e.preventDefault() : undefined}
        onDrop={editable ? (e) => handleDropOnCard(e, pp) : undefined}
      >
        <div className="champ-poke-img-wrap">
          <img
            src={pp.frontUuid ? mfmThumbUrl(pp.frontUuid) : CHAMP_DEFAULT_SPRITE}
            alt={pp.nombrePokemon}
            className="champ-poke-sprite"
            draggable={false}
            onError={(e) => { e.currentTarget.src = CHAMP_DEFAULT_SPRITE; }}
          />
          <span className="champ-poke-lvl">Nv. {pp.nivel}</span>
        </div>
        <div className="champ-poke-body">
          <h6>{pp.nombrePokemon}</h6>
          {pp.apodo && <div className="apodo">"{pp.apodo}"</div>}
          <div className="champ-types">
            {[pp.tipo1, pp.tipo2].filter(Boolean).map((t) => (
              <span key={t} className="champ-type" style={{ background: typeColor(t) }}>{t}</span>
            ))}
          </div>

          {renderEstadoBadges(pp)}

          {/* XP individual del Pokémon */}
          {!pp.esHuevo && (
            <div className="champ-poke-xp-row">
              {editable ? (
                <>
                  <button className="champ-qa-btn minus" onClick={() => adjustPokeXp(pp, -1)} title="-1 XP">−</button>
                  <span className={`champ-poke-xp-val${pp.xpParaEvolucionar != null && pp.experiencia >= pp.xpParaEvolucionar ? " ready" : ""}`}>
                    XP {pp.experiencia}{pp.xpParaEvolucionar != null ? `/${pp.xpParaEvolucionar}` : ""}
                  </span>
                  <button className="champ-qa-btn plus" onClick={() => adjustPokeXp(pp, 1)} title="+1 XP">+</button>
                  <button className="champ-qa-btn plus" onClick={() => adjustPokeXp(pp, 5)} title="+5 XP">+5</button>
                  <button className="champ-qa-btn plus" onClick={() => adjustPokeXp(pp, 10)} title="+10 XP">+10</button>
                </>
              ) : (
                <span className="champ-poke-xp-val">
                  XP {pp.experiencia}{pp.xpParaEvolucionar != null ? `/${pp.xpParaEvolucionar}` : ""}
                </span>
              )}
            </div>
          )}

          {renderPokeControls(pp, inTeam)}
        </div>
      </div>
    );
  }

  return (
    <div className="champ-sheet">
      {/* Estadísticas */}
      <div className="champ-panel">
        <div className="champ-panel-title">
          <span>{character.nombre || "Entrenador"} · {character.rol}</span>
          {editable && !editStats && (
            <MDBBtn size="sm" color="link" onClick={beginEdit}>Editar</MDBBtn>
          )}
        </div>

        {editStats ? (
          <div>
            <MDBInput label="Nombre" className="mb-2" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            <div className="row g-2 mb-2">
              <div className="col">
                <label className="form-label">Región</label>
                <div className="d-flex gap-3 mt-1 flex-wrap">
                  {regionCatalog.map((r) => {
                    const nombre = r.nombre;
                    const checked = !!(form.regionActual && form.regionActual.split(",").map(s => s.trim()).includes(nombre));
                    return (
                      <div className="form-check" key={nombre}>
                        <input className="form-check-input" type="checkbox" id={`chk-region-${nombre}`}
                          checked={checked}
                          onChange={(e) => {
                            const current = form.regionActual ? form.regionActual.split(",").map(s => s.trim()).filter(Boolean) : [];
                            let next;
                            if (e.target.checked) {
                              next = current.includes(nombre) ? current : [...current, nombre];
                            } else {
                              next = current.filter(s => s !== nombre);
                              if (next.length === 0) next = current; // no desactivar la última
                            }
                            setForm({ ...form, regionActual: next.join(",") });
                          }} />
                        <label className="form-check-label" htmlFor={`chk-region-${nombre}`}>
                          {nombre.charAt(0) + nombre.slice(1).toLowerCase()}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="col">
                <MDBInput type="number" label="Nivel" value={form.nivel}
                  onChange={(e) => setForm({ ...form, nivel: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col">
                <MDBInput type="number" label="Experiencia" value={form.experiencia}
                  onChange={(e) => setForm({ ...form, experiencia: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div className="col">
                <MDBInput type="number" label="Dinero" value={form.dinero}
                  onChange={(e) => setForm({ ...form, dinero: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
            <MDBInput textarea label="Notas" rows={3} className="mb-3" value={form.notas}
              onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            <MDBBtn color="secondary" className="me-2" onClick={() => setEditStats(false)}>Cancelar</MDBBtn>
            <MDBBtn className="champ-btn-primary" onClick={saveStats}>Guardar</MDBBtn>
          </div>
        ) : (
          <>
            <div className="champ-stats">
              <div className="champ-stat"><div className="v">{character.nivel}</div><div className="l">Nivel</div></div>
              <div className="champ-stat"><div className="v">{character.regionActual === "KANTO,JOHTO" ? "Kanto + Johto" : character.regionActual === "KANTO" ? "Kanto" : "Johto"}</div><div className="l">Región</div></div>
              {!editable && (
                <>
                  <div className="champ-stat"><div className="v">{character.experiencia}</div><div className="l">Experiencia</div></div>
                  <div className="champ-stat"><div className="v">{character.dinero}₽</div><div className="l">Dinero</div></div>
                </>
              )}
            </div>

            {editable && (
              <div className="champ-qa-block">
                <div className="champ-qa-row">
                  <span className="champ-qa-label">Experiencia</span>
                  <span className="champ-qa-value">{character.experiencia}</span>
                  <div className="champ-qa-btns">
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("experiencia", -20)}>-20</button>
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("experiencia", -5)}>-5</button>
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("experiencia", -1)}>-1</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("experiencia", +1)}>+1</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("experiencia", +5)}>+5</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("experiencia", +20)}>+20</button>
                  </div>
                </div>
                <div className="champ-qa-row">
                  <span className="champ-qa-label">Dinero</span>
                  <span className="champ-qa-value">{character.dinero}₽</span>
                  <div className="champ-qa-btns">
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("dinero", -500)}>-500</button>
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("dinero", -50)}>-50</button>
                    <button type="button" className="champ-qa-btn minus" onClick={() => adjustStat("dinero", -5)}>-5</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("dinero", +5)}>+5</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("dinero", +50)}>+50</button>
                    <button type="button" className="champ-qa-btn plus" onClick={() => adjustStat("dinero", +500)}>+500</button>
                  </div>
                </div>
              </div>
            )}

            {character.notas && <p className="champ-text-muted champ-mt-3">{character.notas}</p>}
          </>
        )}
      </div>

      {/* Equipo */}
      <div className="champ-panel">
        <div className="champ-panel-title">
          <span>Equipo ({(character.equipo || []).length}/6)</span>
          {editable && (
            <div className="champ-panel-actions">
              <MDBBtn size="sm" className="champ-btn-secondary" onClick={healAll} title="Curar todos">💊 Curar todos</MDBBtn>
              <MDBBtn size="sm" className="champ-btn-primary" onClick={() => openPokemonPicker(true)}>+ Pokémon</MDBBtn>
              <MDBBtn size="sm" className="champ-btn-secondary" onClick={() => openEggPicker(true)}>🥚 Huevo</MDBBtn>
            </div>
          )}
        </div>
        <div
          className="champ-dropzone"
          onDragOver={editable ? (e) => e.preventDefault() : undefined}
          onDrop={editable ? () => handleDropOnZone(true) : undefined}
        >
          {(character.equipo || []).length === 0 ? (
            <p className="champ-text-muted">Sin Pokémon en el equipo.</p>
          ) : (
            <div className="champ-poke-grid team">
              {character.equipo.map((pp) => renderPokemon(pp, true))}
            </div>
          )}
        </div>
      </div>

      {/* Caja */}
      <div className="champ-panel">
        <div className="champ-panel-title">
          <span>Caja PC ({(character.caja || []).length})</span>
          {editable && (
            <div className="champ-panel-actions">
              <MDBBtn size="sm" className="champ-btn-primary" onClick={() => openPokemonPicker(false)}>+ Pokémon</MDBBtn>
              <MDBBtn size="sm" className="champ-btn-secondary" onClick={() => openEggPicker(false)}>🥚 Huevo</MDBBtn>
            </div>
          )}
        </div>
        <div
          className="champ-dropzone"
          onDragOver={editable ? (e) => e.preventDefault() : undefined}
          onDrop={editable ? () => handleDropOnZone(false) : undefined}
        >
          {(character.caja || []).length === 0 ? (
            <p className="champ-text-muted">La caja PC está vacía.</p>
          ) : (
            <div className="champ-poke-grid box">
              {character.caja.map((pp) => renderPokemon(pp, false))}
            </div>
          )}
        </div>
      </div>

      {/* Mochila */}
      <div className="champ-panel">
        <div className="champ-panel-title">
          <span>Mochila</span>
          {editable && (
            <MDBBtn size="sm" className="champ-btn-secondary" onClick={() => setPickerType("items")}>+ Objeto</MDBBtn>
          )}
        </div>
        {(character.mochila || []).length === 0 ? (
          <p className="champ-text-muted">La mochila está vacía.</p>
        ) : (
          <div className="champ-item-grid">
            {character.mochila.map((it) => {
              const draggable = editable && it.utilizable && it.cantidad > 0;
              return (
                <div
                  key={it.idPo}
                  className={
                    "champ-item-card" +
                    (it.utilizable ? " utilizable" : "") +
                    (dragItem?.idPo === it.idPo ? " dragging" : "")
                  }
                  draggable={draggable}
                  onDragStart={draggable ? () => setDragItem(it) : undefined}
                  onDragEnd={draggable ? () => setDragItem(null) : undefined}
                  title={draggable ? "Arrástralo sobre un Pokémon para usarlo" : undefined}
                >
                  <div className="champ-item-head">
                    <strong>{it.nombre}</strong>
                    {it.utilizable && <span className="champ-item-usable-badge" title="Utilizable sobre un Pokémon">Utilizable</span>}
                  </div>
                  <div className="champ-text-muted champ-item-type">{it.tipo}</div>
                  {it.descripcion && <div className="champ-text-muted champ-item-desc">{it.descripcion}</div>}
                  <div className="champ-item-foot">
                    {editable && <MDBBtn size="sm" color="link" onClick={() => changeQty(it, -1)}>−</MDBBtn>}
                    <span className="champ-chip">×{it.cantidad}</span>
                    {editable && <MDBBtn size="sm" color="link" onClick={() => changeQty(it, 1)}>+</MDBBtn>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Medallas */}
      <div className="champ-panel">
        <div className="champ-panel-title"><span>Medallas</span></div>
        {badges.length === 0 ? (
          <p className="champ-text-muted">No hay medallas en esta región.</p>
        ) : (
          <div className="champ-badges-grid">
            {badges.map((b) => (
              <div
                key={b.idMedalla}
                className={"champ-badge" + (b.obtenida ? " obtenida" : "")}
                onClick={editable ? () => toggleBadge(b) : undefined}
                style={{ cursor: editable ? "pointer" : "default" }}
              >
                <div style={{ fontSize: "1.4rem" }}>{b.obtenida ? "🏅" : "⚪"}</div>
                <div className="name">{b.nombre}</div>
                <div className="city">{b.ciudad}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tarjetas Journey */}
      <div className="champ-panel">
        <div className="champ-panel-title">
          <span>Tarjetas Journey</span>
          {editable && (
            <MDBBtn size="sm" className="champ-btn-secondary" onClick={() => setPickerType("cards")}>+ Tarjeta</MDBBtn>
          )}
        </div>
        {(character.tarjetas || []).length === 0 ? (
          <p className="champ-text-muted">Sin tarjetas.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem" }}>
            {character.tarjetas.map((c) => (
              <div key={c.idPt} className="champ-card-tag">
                <strong>{c.nombre}</strong>
                <span className="champ-text-muted">{c.clase}</span>
                {editable && (
                  <MDBBtn size="sm" color="link" className="champ-mt-2" onClick={() => dropCard(c)}>Descartar</MDBBtn>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <CatalogPicker
        show={pickerType === "pokemon"}
        setShow={(v) => !v && setPickerType(null)}
        type="pokemon"
        region={character.regionActual && character.regionActual.includes(",") ? null : character.regionActual}
        onPick={handlePickPokemon}
      />
      <CatalogPicker
        show={pickerType === "items"}
        setShow={(v) => !v && setPickerType(null)}
        type="items"
        onPick={handlePickItem}
      />
      <CatalogPicker
        show={pickerType === "cards"}
        setShow={(v) => !v && setPickerType(null)}
        type="cards"
        onPick={handlePickCard}
      />

      <MDBModal show={!!useItemFor} onHide={closeUseItem} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Usar objeto sobre {useItemFor?.apodo || useItemFor?.nombrePokemon}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeUseItem} />
            </MDBModalHeader>
            <MDBModalBody>
              {(() => {
                const usables = (character.mochila || []).filter((it) => it.utilizable && it.cantidad > 0);
                if (usables.length === 0) return <p className="champ-text-muted">No tienes objetos utilizables.</p>;
                return (
                  <div className="champ-item-grid">
                    {usables.map((it) => (
                      <div
                        key={it.idPo}
                        className="champ-item-card utilizable champ-item-pick"
                        onClick={async () => {
                          const pp = useItemFor;
                          setUseItemFor(null);
                          await applyItem(it.idObjeto, pp.idPp);
                        }}
                      >
                        <div className="champ-item-head">
                          <strong>{it.nombre}</strong>
                          <span className="champ-chip">×{it.cantidad}</span>
                        </div>
                        {it.descripcion && <div className="champ-text-muted champ-item-desc">{it.descripcion}</div>}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={closeUseItem}>Cerrar</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MDBModal show={!!estadoPickerFor} onHide={closeEstadoPicker} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Añadir estado a {estadoPickerFor?.apodo || estadoPickerFor?.nombrePokemon}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeEstadoPicker} />
            </MDBModalHeader>
            <MDBModalBody>
              {estadoCatalog.length === 0 ? (
                <p className="champ-text-muted">No hay estados disponibles.</p>
              ) : (
                <div className="champ-estado-picker">
                  {estadoCatalog.map((es) => {
                    const already = (estadoPickerFor?.estados || []).some((x) => x.idEstado === es.idEstado);
                    return (
                      <button
                        key={es.idEstado}
                        type="button"
                        className="champ-estado-badge champ-estado-pick"
                        style={{ background: es.color || "#777", opacity: already ? 0.4 : 1 }}
                        disabled={already}
                        title={es.descripcion || es.nombre}
                        onClick={() => handleAddEstado(estadoPickerFor.idPp, es.idEstado)}
                      >
                        {es.icono ? es.icono + " " : ""}{es.nombre}
                      </button>
                    );
                  })}
                </div>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={closeEstadoPicker}>Cerrar</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <CatalogPicker
        show={showEggPicker}
        setShow={(v) => !v && setShowEggPicker(false)}
        type="pokemon"
        onPick={handlePickEgg}
      />

      <CatalogPicker
        show={showHatchPicker}
        setShow={(v) => !v && closeHatchPicker()}
        type="pokemon"
        onPick={handleHatchPick}
      />

      <MDBModal show={showEggTurnosModal} onHide={closeEggTurnosModal} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>🥚 Añadir Huevo Misterioso</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeEggTurnosModal} />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBInput
                label="Turnos para eclosionar"
                type="number"
                min={1}
                value={eggTurnos}
                onChange={(e) => setEggTurnos(Math.max(1, parseInt(e.target.value) || 1))}
                onKeyDown={(e) => { if (e.key === "Enter") confirmEggTurnos(); }}
                style={{ marginBottom: "1rem" }}
              />
              <p className="champ-text-muted" style={{ marginBottom: "0.5rem", fontWeight: "600" }}>
                ¿Cuándo se elige el Pokémon?
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {[
                  { value: "AHORA",    label: "🔍 Al coger el huevo (elegir ahora)" },
                  { value: "AL_ABRIR", label: "🐣 Al abrir el huevo (elegir al eclosionar)" },
                  { value: "ALEATORIO", label: "🎲 Aleatorio (sorpresa al eclosionar)" },
                ].map(({ value, label }) => (
                  <label key={value} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input
                      type="radio"
                      name="eggMode"
                      value={value}
                      checked={eggMode === value}
                      onChange={() => setEggMode(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn size="sm" color="secondary" outline onClick={closeEggTurnosModal}>Cancelar</MDBBtn>
              <MDBBtn size="sm" color="warning" onClick={confirmEggTurnos}>
                {eggMode === "AHORA" ? "Elegir Pokémon →" : "Añadir Huevo"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MDBModal show={!!detailPoke} onHide={closeDetail} tabIndex="-1">
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {detailPoke?.nombrePokemon || "Pokémon"}
                {detailPoke?.apodo ? ` · "${detailPoke.apodo}"` : ""}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeDetail} />
            </MDBModalHeader>
            <MDBModalBody>
              {detailLoading || detailPoke?.loading ? (
                <p className="champ-text-muted">Cargando…</p>
              ) : detailPoke ? (
                <div className="champ-detail">
                  <div className="champ-detail-imgs">
                    <img
                      src={detailPoke.frontUuid ? mfmMediumUrl(detailPoke.frontUuid) : CHAMP_DEFAULT_SPRITE}
                      alt="Frente"
                      onError={(e) => { e.currentTarget.src = CHAMP_DEFAULT_SPRITE; }}
                    />
                    <img
                      src={detailPoke.backUuid ? mfmMediumUrl(detailPoke.backUuid) : CHAMP_DEFAULT_SPRITE}
                      alt="Reverso"
                      onError={(e) => { e.currentTarget.src = CHAMP_DEFAULT_SPRITE; }}
                    />
                  </div>
                  <div className="champ-types">
                    {[detailPoke.tipo1, detailPoke.tipo2].filter(Boolean).map((t) => (
                      <span key={t} className="champ-type" style={{ background: typeColor(t) }}>{t}</span>
                    ))}
                  </div>
                  <div className="champ-detail-stats">
                    <div><strong>PS</strong> {detailPoke.ps ?? "—"}</div>
                    <div><strong>Ataque</strong> {detailPoke.ataqueBase ?? "—"}</div>
                    <div><strong>Crítico</strong> {detailPoke.criticoDado ?? "—"}</div>
                    {detailPoke.fase != null && <div><strong>Fase</strong> {detailPoke.fase}</div>}
                    {detailPoke.region && <div><strong>Región</strong> {detailPoke.region}</div>}
                  </div>
                  {Array.isArray(detailPoke.debilidades) && detailPoke.debilidades.length > 0 && (
                    <p className="champ-detail-line"><strong>Debilidades:</strong> {detailPoke.debilidades.join(", ")}</p>
                  )}
                  {Array.isArray(detailPoke.inmunidades) && detailPoke.inmunidades.length > 0 && (
                    <p className="champ-detail-line"><strong>Inmunidades:</strong> {detailPoke.inmunidades.join(", ")}</p>
                  )}
                  {Array.isArray(detailPoke.mos) && detailPoke.mos.length > 0 && (
                    <p className="champ-detail-line"><strong>MO:</strong> {detailPoke.mos.join(", ")}</p>
                  )}
                  {detailPoke.pokedexTexto && (
                    <p className="champ-detail-pokedex">{detailPoke.pokedexTexto}</p>
                  )}
                  {(() => {
                    const livePp = detailPoke.pp
                      ? [...(character.equipo || []), ...(character.caja || [])].find((p) => p.idPp === detailPoke.pp.idPp)
                      : null;
                    if (!livePp) return null;
                    const usables = (character.mochila || []).filter((it) => it.utilizable && it.cantidad > 0);
                    return (
                      <div className="champ-detail-controls">
                        {renderPokeControls(livePp, livePp.enEquipo, { showDetailBtn: false })}
                        {renderEstadoBadges(livePp, { removable: true })}
                        {editable && (
                          <div className="champ-detail-useitem">
                            <MDBBtn
                              size="sm"
                              className="champ-btn-secondary"
                              disabled={usables.length === 0}
                              onClick={() => setUseItemFor(livePp)}
                              title={usables.length === 0 ? "No tienes objetos utilizables" : "Usar un objeto sobre este Pokémon"}
                            >
                              💼 Usar objeto
                            </MDBBtn>
                            {Array.isArray(detailPoke.evoluciones) && detailPoke.evoluciones.length > 0 && (
                              <MDBBtn
                                size="sm"
                                className="champ-btn-evolve"
                                onClick={() => openEvoModal(livePp)}
                              >
                                ⬆️ Evolucionar
                              </MDBBtn>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </MDBModalBody>
            <MDBModalFooter>
              {isAdmin && detailPoke && !detailPoke.loading && (
                <MDBBtn color="warning" size="sm" onClick={() => openAdminEdit(detailPoke)}>
                  ✏️ Editar catálogo
                </MDBBtn>
              )}
              <MDBBtn color="secondary" onClick={closeDetail}>Cerrar</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* ── Modal de Evolución ─────────────────────────────────────────── */}
      <MDBModal show={evoModal} onHide={closeEvoModal} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>⬆️ Evolucionar Pokémon</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeEvoModal} />
            </MDBModalHeader>
            <MDBModalBody>
              {evoResult ? (
                /* Resultado post-evolución */
                <div className="champ-evo-result">
                  <p className="champ-evo-success">✅ ¡{evoSelected?.nombre} ha evolucionado!</p>
                  {evoResult.advertencias?.length > 0 && (
                    <div className="champ-evo-warnings">
                      {evoResult.advertencias.map((w, i) => (
                        <p key={i} className="champ-evo-warning">⚠️ {w}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Selección de evolución (si hay varias) */}
                  {evoOptions.length > 1 && (
                    <div className="champ-evo-options">
                      <p className="champ-text-muted" style={{ fontSize: "0.82rem", marginBottom: "0.5rem" }}>
                        Elige la evolución:
                      </p>
                      {evoOptions.map((evo) => (
                        <button
                          key={evo.idPokemon}
                          type="button"
                          className={`champ-evo-option${evoSelected?.idPokemon === evo.idPokemon ? " selected" : ""}`}
                          onClick={() => setEvoSelected(evo)}
                        >
                          {modoEvolucion !== "OCULTA" && (
                            <>
                              <span className="champ-evo-option-name">{evo.nombre}</span>
                              <div className="champ-types" style={{ justifyContent: "flex-start" }}>
                                {[evo.tipo1, evo.tipo2].filter(Boolean).map((t) => (
                                  <span key={t} className="champ-type" style={{ background: typeColor(t) }}>{t}</span>
                                ))}
                              </div>
                            </>
                          )}
                          {(() => {
                            const req = getReqLabel(evo, evoTargetPp);
                            return (
                              <span className={`champ-evo-option-req${req.ok === true ? " ok" : req.ok === false ? " warn" : " info"}`}>
                                {req.text}
                              </span>
                            );
                          })()}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Resumen de requisitos */}
                  {evoSelected && (
                    <div className="champ-evo-reqs">
                      {modoEvolucion !== "OCULTA" && (
                        <p className="champ-evo-option-name" style={{ marginBottom: "0.3rem" }}>{evoSelected.nombre}</p>
                      )}
                      {checkRequisitos(evoSelected, evoTargetPp).map((c, i) => (
                        <p key={i} className={`champ-evo-req ${c.ok === true ? "ok" : c.ok === false ? "warn" : "info"}`}>
                          {c.msg}
                        </p>
                      ))}
                      <p className="champ-text-muted" style={{ fontSize: "0.78rem", marginTop: "0.4rem" }}>
                        Los recursos necesarios se descontarán automáticamente.
                      </p>
                    </div>
                  )}
                </>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              {evoResult ? (
                <MDBBtn color="primary" onClick={closeEvoModal}>Cerrar</MDBBtn>
              ) : (
                <>
                  <MDBBtn color="secondary" onClick={closeEvoModal}>Cancelar</MDBBtn>
                  <MDBBtn
                    color="warning"
                    disabled={!evoSelected || evoLoading}
                    onClick={confirmEvolve}
                  >
                    {evoLoading ? "Evolucionando…" : "⬆️ Confirmar evolución"}
                  </MDBBtn>
                </>
              )}
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* ── Modal Admin: editar Pokémon del catálogo ──────────────────── */}
      <MDBModal show={!!adminEditPoke} onHide={closeAdminEdit} tabIndex="-1">
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>✏️ Editar catálogo — {adminEditPoke?.nombre}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeAdminEdit} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="champ-admin-form">
                <div className="champ-admin-row">
                  <MDBInput label="Nombre" value={adminEditForm.nombre || ""} onChange={(e) => adminFieldChange("nombre", e.target.value)} />
                  <MDBInput label="Tipo 1" value={adminEditForm.tipo1 || ""} onChange={(e) => adminFieldChange("tipo1", e.target.value)} />
                  <MDBInput label="Tipo 2" value={adminEditForm.tipo2 || ""} onChange={(e) => adminFieldChange("tipo2", e.target.value)} />
                </div>
                <div className="champ-admin-row">
                  <MDBInput type="number" label="Fase" value={adminEditForm.fase ?? ""} onChange={(e) => adminFieldChange("fase", e.target.value)} />
                  <MDBInput label="Región" value={adminEditForm.region || ""} onChange={(e) => adminFieldChange("region", e.target.value)} />
                  <MDBInput type="number" label="PS base" value={adminEditForm.ps ?? ""} onChange={(e) => adminFieldChange("ps", e.target.value)} />
                </div>
                <div className="champ-admin-row">
                  <MDBInput label="Ataque base" value={adminEditForm.ataqueBase || ""} onChange={(e) => adminFieldChange("ataqueBase", e.target.value)} />
                  <MDBInput label="Crítico dado" value={adminEditForm.criticoDado || ""} onChange={(e) => adminFieldChange("criticoDado", e.target.value)} />
                </div>
                <hr className="champ-admin-sep" />
                <p className="champ-admin-section-title">Evolución</p>
                <div className="champ-admin-row">
                  <CatalogAutocomplete
                    label="Evoluciona de (Pokémon)"
                    value={adminEditForm.evolucionaDe}
                    onChange={(id) => adminFieldChange("evolucionaDe", id)}
                    options={adminPokeOptions}
                  />
                  <MDBInput label="Método evolución" value={adminEditForm.metodoEvolucion || ""} onChange={(e) => adminFieldChange("metodoEvolucion", e.target.value)} />
                  <MDBInput type="number" label="XP evolución" value={adminEditForm.xpEvolucion ?? ""} onChange={(e) => adminFieldChange("xpEvolucion", e.target.value)} />
                </div>
                <div className="champ-admin-row">
                  <CatalogAutocomplete
                    label="Objeto evolución"
                    value={adminEditForm.idObjetoEvolucion}
                    onChange={(id) => adminFieldChange("idObjetoEvolucion", id)}
                    options={adminItemOptions}
                  />
                  <MDBInput label="Descripción requisito" value={adminEditForm.descripcionRequisito || ""} onChange={(e) => adminFieldChange("descripcionRequisito", e.target.value)} />
                </div>
                <hr className="champ-admin-sep" />
                <p className="champ-admin-section-title">Tipos relacionados (separados por coma)</p>
                <div className="champ-admin-row">
                  <MDBInput label="Debilidades" value={adminEditForm.debilidades || ""} onChange={(e) => adminFieldChange("debilidades", e.target.value)} />
                  <MDBInput label="Inmunidades" value={adminEditForm.inmunidades || ""} onChange={(e) => adminFieldChange("inmunidades", e.target.value)} />
                  <MDBInput label="MO" value={adminEditForm.mos || ""} onChange={(e) => adminFieldChange("mos", e.target.value)} />
                </div>
                <hr className="champ-admin-sep" />
                <p className="champ-admin-section-title">Imágenes (UUID MFM)</p>
                <div className="champ-admin-row">
                  <MDBInput label="Front UUID" value={adminEditForm.frontUuid || ""} onChange={(e) => adminFieldChange("frontUuid", e.target.value)} />
                  <MDBInput label="Back UUID" value={adminEditForm.backUuid || ""} onChange={(e) => adminFieldChange("backUuid", e.target.value)} />
                </div>
                <div className="champ-admin-row">
                  <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.88rem" }}>
                    <input type="checkbox" checked={!!adminEditForm.inicial} onChange={(e) => adminFieldChange("inicial", e.target.checked)} />
                    Pokémon inicial
                  </label>
                </div>
                <div style={{ marginTop: "0.75rem" }}>
                  <MDBTextArea label="Texto Pokédex" rows={3} value={adminEditForm.pokedexTexto || ""} onChange={(e) => adminFieldChange("pokedexTexto", e.target.value)} />
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={closeAdminEdit}>Cancelar</MDBBtn>
              <MDBBtn color="primary" disabled={adminEditLoading} onClick={saveAdminEdit}>
                {adminEditLoading ? "Guardando…" : "💾 Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
}
